// PanelSheet — the one place a choice is made.
//
// Verbs live in cards, nouns live on lanes, choices live here. It DOCKS into
// the cards column rather than floating over the board, so the rail chips, the
// timeline and any live alarm stay visible and updating while you work. A
// centred modal would cover the thing you are reasoning about.
//
// ⚠️ THE FRICTION MODEL. There is no "Are you sure?" here. A yes/no on a
// frequent action is exactly what trains an operator to click through the one
// that mattered. Instead, taking over an occupied room REQUIRES a second
// decision — where does the displaced person go — before anything is written.
// The friction is a question that has to be answered, not a question that has
// to be dismissed.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { MONO } from "./panelCard.styles";
import { DISPLACED_OPTIONS } from "../../domain/panelAssign";

const INK = "#0f172a";
const INK2 = "#475569";
const LINE = "#cbd5e1";
const BLUE = "#2563eb";

// 44px minimum, absolute — a finger is not typographic, so this one value does
// NOT scale with --u.
const ROW_MIN = 44;

function Row({ label, detail, badge, selected, onSelect, disabled }) {
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        aria-disabled={disabled ? "true" : undefined}
        aria-current={selected ? "true" : undefined}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "calc(var(--u) * 0.4)",
          width: "100%",
          minHeight: ROW_MIN,
          padding: "calc(var(--u) * 0.35) calc(var(--u) * 0.5)",
          background: selected ? "#eaf0fd" : "transparent",
          border: "none",
          borderLeft: `3px solid ${selected ? BLUE : "transparent"}`,
          font: "inherit",
          textAlign: "left",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.5 : 1,
          color: INK,
        }}
      >
        <span style={{ fontWeight: 700 }}>{label}</span>
        {detail ? (
          <span style={{ color: INK2, fontSize: "calc(var(--u) * 0.62)" }}>{detail}</span>
        ) : null}
        {badge ? (
          <span
            style={{
              marginLeft: "auto",
              fontFamily: MONO,
              fontSize: "calc(var(--u) * 0.6)",
              color: INK2,
            }}
          >
            {badge}
          </span>
        ) : null}
      </button>
    </li>
  );
}

function Group({ title, children }) {
  if (!children || (Array.isArray(children) && children.length === 0)) return null;
  return (
    <>
      <li
        aria-hidden="true"
        style={{
          padding: "calc(var(--u) * 0.4) calc(var(--u) * 0.5) calc(var(--u) * 0.15)",
          fontSize: "calc(var(--u) * 0.55)",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: INK2,
        }}
      >
        {title}
      </li>
      {children}
    </>
  );
}

export default function PanelSheet({
  // Person-first ("Where does Larch go?") when `person` is set; room-first
  // ("Who goes into WOR 4?") when `lane` is set. Both end in the same place:
  // a person, a room, and — if that room was occupied — a decision about the
  // person coming out of it.
  person,
  lane,
  bench = [],
  destinations,
  facilities = [],
  busy = false,
  error = null,
  onCommit,
  onClose,
}) {
  const [choice, setChoice] = useState(null);      // { kind, lane, displaced }
  const [disposition, setDisposition] = useState(null);
  const [travelDest, setTravelDest] = useState("");
  const headingRef = useRef(null);
  const errorRef = useRef(null);

  // Focus the heading on open and on every step change, so a screen reader
  // hears where it is. This is deliberately NOT a new live region: the panel
  // already has exactly one assertive region (the staleness band) and one
  // status region (the off-page line), and a third would make all three
  // ambiguous. Results are announced by MOVING FOCUS instead.
  useEffect(() => {
    if (headingRef.current) headingRef.current.focus();
  }, [choice]);

  useEffect(() => {
    if (error && errorRef.current) errorRef.current.focus();
  }, [error]);

  const onKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape" && !busy) {
        e.stopPropagation();
        onClose();
      }
    },
    [busy, onClose]
  );

  const roomFirst = Boolean(lane && !person);
  const [picked, setPicked] = useState(null);   // room-first: who is going in

  const step = choice && choice.displaced ? "displaced" : "destination";

  const commit = () => {
    if (busy) return;
    onCommit({
      person: picked,
      lane: choice.lane,
      displaced: choice.displaced || null,
      disposition: choice.displaced ? disposition : null,
      travelDest,
      travelDestName:
        (facilities.find((f) => f && f.id === travelDest) || {}).name || travelDest,
    });
  };

  const canCommit =
    step === "destination"
      ? false // an open room commits on selection; nothing to confirm
      : Boolean(disposition && (disposition !== "transfer" || travelDest));

  return (
    // Sheet container with onKeyDown — the keyboard path, not a mouse-only trap.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <section
      onKeyDown={onKeyDown}
      aria-label="Place a provider"
      data-testid="panel-sheet"
      style={{
        border: `1px solid ${LINE}`,
        borderRadius: "calc(var(--u) * 0.25)",
        background: "#ffffff",
        boxShadow: "0 calc(var(--u) * 0.2) calc(var(--u) * 0.6) rgba(15,23,42,0.10)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <header
        style={{
          padding: "calc(var(--u) * 0.4) calc(var(--u) * 0.5)",
          borderBottom: `1px solid ${LINE}`,
          background: "#f8fafc",
        }}
      >
        <div
          style={{
            fontFamily: MONO,
            fontSize: "calc(var(--u) * 0.55)",
            letterSpacing: "0.1em",
            color: INK2,
          }}
        >
          {roomFirst ? `ROOM · ${lane.roomName}` : `PROVIDER · ${person?.name}`}
        </div>
        <h2
          ref={headingRef}
          tabIndex={-1}
          style={{
            margin: 0,
            fontSize: "calc(var(--u) * 0.8)",
            outline: "none",
          }}
        >
          {step === "destination"
            ? roomFirst
              ? `Who goes into ${lane.roomName}?`
              : `Where does ${person?.name} go?`
            : `${choice.displaced.name} comes out of ${choice.lane.roomName}. Where do they go?`}
        </h2>
      </header>

      {error ? (
        <div
          ref={errorRef}
          tabIndex={-1}
          data-testid="sheet-error"
          style={{
            padding: "calc(var(--u) * 0.4) calc(var(--u) * 0.5)",
            background: "#fef2f2",
            borderBottom: `2px solid #dc2626`,
            color: "#7f1d1d",
            fontWeight: 700,
            fontSize: "calc(var(--u) * 0.66)",
            outline: "none",
          }}
        >
          {error}
        </div>
      ) : null}

      <ul style={{ listStyle: "none", margin: 0, padding: 0, maxHeight: "calc(var(--u) * 14)", overflowY: "auto", flex: "1 1 auto" }}>
        {step === "destination" && roomFirst ? (
          <>
            <Group title="On the bench">
              {bench.map((p) => (
                <Row
                  key={p.id}
                  label={p.name}
                  detail={String(p.subRole || "CRNA").toUpperCase()}
                  disabled={busy}
                  onSelect={() => {
                    setPicked(p);
                    // Occupied? Then the displaced question comes first, and
                    // nothing is written until it is answered.
                    if (lane.holderId) {
                      setChoice({
                        lane,
                        displaced: { id: lane.holderId, name: lane.holderName, subRole: "crna" },
                      });
                    } else {
                      onCommit({ person: p, lane, displaced: null, disposition: null, travelDest: "" });
                    }
                  }}
                />
              ))}
            </Group>

            {/* Emptying a seat is a real action, not the absence of one. */}
            <Group title="Or">
              <Row
                label="Take the room empty"
                detail="Nobody assigned"
                disabled={busy}
                onSelect={() => {
                  setPicked(null);
                  if (lane.holderId) {
                    setChoice({
                      lane,
                      displaced: { id: lane.holderId, name: lane.holderName, subRole: "crna" },
                    });
                  } else {
                    onCommit({ person: null, lane, displaced: null, disposition: null, travelDest: "" });
                  }
                }}
              />
            </Group>
          </>
        ) : step === "destination" ? (
          <>
            <Group title="Open rooms">
              {destinations.open.map((d) => (
                <Row
                  key={d.roomId}
                  label={d.roomName}
                  badge="no CRNA"
                  disabled={busy}
                  // An empty room writes immediately: frequent, additive, and
                  // corrected by doing it again.
                  onSelect={() =>
                    onCommit({ lane: d, displaced: null, disposition: null, travelDest: "" })
                  }
                />
              ))}
            </Group>

            <Group title="Take over a room">
              {destinations.occupied.map((d) => (
                <Row
                  key={d.roomId}
                  label={d.roomName}
                  badge={`${d.holderName} →`}
                  disabled={busy}
                  // Never writes here. Advances to the required decision.
                  onSelect={() =>
                    setChoice({
                      lane: d,
                      displaced: { id: d.holderId, name: d.holderName, subRole: "crna" },
                    })
                  }
                />
              ))}
            </Group>
          </>
        ) : (
          DISPLACED_OPTIONS.map((o) => (
            <Row
              key={o.key}
              label={o.label}
              detail={o.detail}
              selected={disposition === o.key}
              disabled={busy}
              onSelect={() => setDisposition(o.key)}
            />
          ))
        )}
      </ul>

      {step === "displaced" && disposition === "transfer" ? (
        <div style={{ padding: "calc(var(--u) * 0.4) calc(var(--u) * 0.5)" }}>
          <label style={{ display: "grid", gap: "calc(var(--u) * 0.2)", fontSize: "calc(var(--u) * 0.62)" }}>
            Destination facility
            <select
              value={travelDest}
              onChange={(e) => setTravelDest(e.target.value)}
              style={{ minHeight: ROW_MIN, font: "inherit", padding: "0 calc(var(--u) * 0.4)" }}
            >
              <option value="">—</option>
              {facilities.map((f) => (
                <option key={f.id} value={f.id}>{f.name || f.id}</option>
              ))}
            </select>
          </label>
        </div>
      ) : null}

      <footer
        style={{
          display: "flex",
          gap: "calc(var(--u) * 0.4)",
          padding: "calc(var(--u) * 0.4) calc(var(--u) * 0.5)",
          borderTop: `1px solid ${LINE}`,
        }}
      >
        {step === "displaced" ? (
          <button
            type="button"
            onClick={() => { setChoice(null); setDisposition(null); }}
            style={{ minHeight: ROW_MIN, font: "inherit", background: "none", border: "none", cursor: "pointer", color: INK2 }}
          >
            ← Back
          </button>
        ) : null}

        <button
          type="button"
          onClick={onClose}
          style={{ minHeight: ROW_MIN, marginLeft: "auto", font: "inherit", background: "none", border: `1px solid ${LINE}`, borderRadius: "calc(var(--u) * 0.2)", padding: "0 calc(var(--u) * 0.6)", cursor: "pointer" }}
        >
          Cancel
        </button>

        {step === "displaced" ? (
          <button
            type="button"
            onClick={commit}
            // aria-disabled, NOT disabled: a `disabled` button drops focus to
            // <body> mid-action, which strands a keyboard or screen-reader user.
            aria-disabled={!canCommit || busy ? "true" : undefined}
            onKeyDown={(e) => { if (!canCommit && (e.key === "Enter" || e.key === " ")) e.preventDefault(); }}
            style={{
              minHeight: ROW_MIN,
              font: "inherit",
              fontWeight: 700,
              background: canCommit && !busy ? BLUE : "#94a3b8",
              color: "#ffffff",
              border: "none",
              borderRadius: "calc(var(--u) * 0.2)",
              padding: "0 calc(var(--u) * 0.7)",
              cursor: canCommit && !busy ? "pointer" : "not-allowed",
            }}
          >
            {busy ? "Placing…" : "Place"}
          </button>
        ) : null}
      </footer>
    </section>
  );
}
